<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { DashEnvironmentReading } from 'src/components/Dashboards/dash-environment-helpers';
	import { devices } from 'src/stores/lists/devices';
	import { edit } from 'src/stores/ui/edit_lists';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { DashParameterWidget, Text } from '@Shelly/shelly-design-system';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import {
		getEnvironmentParameters,
		getEnvironmentReadingsForCards,
	} from 'src/components/Dashboards/dash-environment-helpers';

	export let cards: { id: string | number; position?: number }[] = [];
	export let staticPreview = false;
	export let horizontal = false;

	$: memberIds = cards.map((card) => String(card.id));
	$: memberStatusStore = createMemberStatusSnapshot(memberIds);
	$: memberSettingsStore = createMemberSettingsSnapshot(memberIds);
	$: readings = getEnvironmentReadingsForCards(
		cards,
		$devices,
		$memberStatusStore,
		$memberSettingsStore,
	);
	$: emptyMessage = $format('dash_environment_empty_message', {
		default: 'No temperature or humidity readings available from the selected devices.',
	});
	$: previewReadings = [
		{
			deviceId: 'preview-1',
			title: $format('dash_environment_preview_hnt', { default: 'Bathroom H&T' }),
			temperature: '39°C',
			humidity: '59%',
			battery: '3%',
			hasTemperature: true,
			hasHumidity: true,
			hasBattery: true,
			batteryIcon: 'fa-solid fa-battery-quarter',
			online: true,
		},
	] satisfies DashEnvironmentReading[];
	$: displayReadings = staticPreview ? previewReadings : readings;
	$: parameterLabels = {
		temperature: $format('label_temperature', { default: 'Temperature' }),
		humidity: $format('label_humidity', { default: 'Humidity' }),
		battery: $format('icon_name_battery', { default: 'Battery' }),
	};
	$: missingValue = $format('not_available_short', { default: 'N/A' });
	$: offlineText = $format('label_offline', { default: 'Offline' });

	const openDeviceDrawer = (deviceId: string) => {
		if (staticPreview || $edit) return;
		setSidePanelState({ type: 'device', id: deviceId, open: true });
	};
</script>

{#if displayReadings.length}
	<div class="dash-environment-list" class:dash-environment-list--horizontal={horizontal}>
		{#each displayReadings as reading (reading.deviceId)}
			{@const parameters = getEnvironmentParameters(reading, parameterLabels, missingValue)}
			<DashParameterWidget
				title={reading.title}
				{parameters}
				offline={!reading.online}
				{offlineText}
				isFocusable={!staticPreview && !$edit}
				on:click={() => openDeviceDrawer(reading.deviceId)}
			/>
		{/each}
	</div>
{:else}
	<div class="dash-environment-empty">
		<Text styleType="body-small" class="text-color-muted text-center">{emptyMessage}</Text>
	</div>
{/if}

<style lang="scss">
	.dash-environment-list {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
		gap: var(--spacing-s);
		width: 100%;
		min-width: 0;
		max-width: 100%;
	}

	.dash-environment-list--horizontal {
		grid-template-columns: none;
		grid-auto-flow: column;
		grid-auto-columns: minmax(260px, 320px);
		justify-content: start;
		overflow-x: auto;
		overscroll-behavior-inline: contain;
		scroll-snap-type: inline proximity;
		padding-bottom: var(--spacing-xs);
		scrollbar-width: none;
	}

	.dash-environment-list--horizontal::-webkit-scrollbar {
		display: none;
	}

	.dash-environment-list--horizontal :global(.dash-parameter-widget-surface) {
		scroll-snap-align: start;
	}

	.dash-environment-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 120px;
		padding: var(--spacing-m);
	}
</style>
